import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import { awsConfig } from "../aws-config";
import { getUploadUrl, uploadFileToS3 } from "../api";

const CATEGORIES = [
  "Electrical",
  "Cleaning",
  "Plumbing",
  "Outdoor",
  "Interior Design",
  "Carpentry",
  "Painting",
  "Moving",
];

export default function ProProfileSetupPage() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [category, setCategory] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [city, setCity] = useState("");
  const [experience, setExperience] = useState("");
  const [responseTime, setResponseTime] = useState("1 hour");
  const [phone, setPhone] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);

  async function handleSave() {
    if (!name || !bio || !category || !hourlyRate || !city || !experience) {
      setError("Please fill in all fields");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      let photoUrl = "";
      if (photo) {
        setUploading(true);
        const { uploadUrl, fileUrl } = await getUploadUrl(
          photo.name,
          photo.type,
          "profile-photos",
        );
        await uploadFileToS3(uploadUrl, photo);
        photoUrl = fileUrl;
        setUploading(false);
      }

      await axios.put(
        `${awsConfig.apiUrl}/pro/profile`,
        {
          name,
          bio,
          serviceCategory: category,
          hourlyRate: parseFloat(hourlyRate),
          city,
          experience,
          responseTime,
          phone,
          photoUrl,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const uid = localStorage.getItem("mouna_user_id") || "default";
      localStorage.setItem("mouna_profile_complete_" + uid, "true");
      window.location.href = "/";
    } catch (err: any) {
      console.error("Save error:", err);
      console.error("Response:", err.response?.data);
      setError(
        err.response?.data?.error ||
          "Failed to save profile. Please try again.",
      );
      setUploading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-4 pb-5 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-mouna-blue rounded-xl flex items-center justify-center">
            <span className="text-white font-bold">M</span>
          </div>
          <span className="font-display font-bold text-navy">Mouna Pro</span>
        </div>
        <div className="text-xs text-mouna-blue font-bold uppercase tracking-wider mb-1">
          STEP {step} OF 2
        </div>
        <h1 className="font-display text-2xl font-bold text-navy mb-1">
          {step === 1 ? "Set up your profile" : "Your rates & availability"}
        </h1>
        <p className="text-xs text-gray-400">
          {step === 1
            ? "Tell clients about yourself."
            : "Set your hourly rate and response time."}
        </p>
        <div className="mt-3 bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-mouna-blue h-1.5 rounded-full transition-all duration-300"
            style={{ width: step === 1 ? "50%" : "100%" }}
          />
        </div>
      </div>

      <div className="px-5 pt-5 pb-24">
        {error && (
          <div className="bg-red-50 text-red-500 text-sm rounded-xl p-3 mb-4">
            {error}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            {/* Profile photo */}
            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                Profile Photo
              </label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl bg-blue-pale flex items-center justify-center overflow-hidden flex-shrink-0">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl">👷</span>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    id="photo-upload"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setPhoto(file);
                        setPhotoPreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                  <label
                    htmlFor="photo-upload"
                    className="bg-mouna-blue text-white rounded-full px-4 py-2 text-xs font-bold cursor-pointer"
                  >
                    Choose Photo
                  </label>
                  <p className="text-xs text-gray-400 mt-1">
                    JPG or PNG, max 5MB
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                Full Name *
              </label>
              <input
                type="text"
                placeholder="e.g. Marcus Thorne"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue"
              />
            </div>

            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                Service Category *
              </label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    className={
                      "py-3 px-4 rounded-xl text-sm font-semibold border-2 transition-all text-left " +
                      (category === cat
                        ? "bg-mouna-blue text-white border-mouna-blue"
                        : "bg-white text-gray-500 border-gray-200")
                    }
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                About You *
              </label>
              <textarea
                placeholder="Describe your expertise..."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue resize-none"
              />
            </div>

            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                City *
              </label>
              <input
                type="text"
                placeholder="e.g. New York"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue"
              />
            </div>

            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                Phone (for SMS alerts)
              </label>
              <input
                type="tel"
                placeholder="+12125551234"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-mouna-blue"
              />
            </div>

            <button
              onClick={() => {
                if (!name || !category || !bio || !city) {
                  setError("Please fill in all required fields");
                  return;
                }
                setError("");
                setStep(2);
              }}
              className="w-full bg-mouna-blue text-white rounded-full py-3 font-bold text-sm"
            >
              Continue →
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                Hourly Rate (USD) *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                  $
                </span>
                <input
                  type="number"
                  placeholder="85"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm outline-none focus:border-mouna-blue"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                Years of Experience *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  "1-2 Years",
                  "3-5 Years",
                  "5-8 Years",
                  "8-10 Years",
                  "10+ Years",
                  "15+ Years",
                ].map((exp) => (
                  <button
                    key={exp}
                    onClick={() => setExperience(exp)}
                    className={
                      "py-2 px-3 rounded-xl text-xs font-semibold border-2 transition-all " +
                      (experience === exp
                        ? "bg-mouna-blue text-white border-mouna-blue"
                        : "bg-white text-gray-500 border-gray-200")
                    }
                  >
                    {exp}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                Response Time
              </label>
              <div className="grid grid-cols-2 gap-2">
                {["30 minutes", "1 hour", "2 hours", "Same day"].map((rt) => (
                  <button
                    key={rt}
                    onClick={() => setResponseTime(rt)}
                    className={
                      "py-2 px-3 rounded-xl text-xs font-semibold border-2 transition-all " +
                      (responseTime === rt
                        ? "bg-mouna-blue text-white border-mouna-blue"
                        : "bg-white text-gray-500 border-gray-200")
                    }
                  >
                    {rt}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-3">
                PROFILE PREVIEW
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-blue-pale flex items-center justify-center overflow-hidden">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xl">👷</span>
                  )}
                </div>
                <div>
                  <div className="font-bold text-navy">
                    {name || "Your Name"}
                  </div>
                  <div className="text-xs text-mouna-blue font-semibold">
                    {category || "Your Category"}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 mb-2">
                {bio || "Your bio..."}
              </p>
              <div className="flex gap-3">
                <span className="text-xs text-gray-500">
                  💰 ${hourlyRate || "0"}/hr
                </span>
                <span className="text-xs text-gray-500">
                  📍 {city || "City"}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 bg-gray-100 text-gray-500 rounded-full py-3 font-semibold text-sm"
              >
                ← Back
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-mouna-blue text-white rounded-full py-3 font-bold text-sm"
              >
                {uploading
                  ? "Uploading photo..."
                  : loading
                    ? "Saving..."
                    : "Complete Profile ✓"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
